import React from 'react';
import classNames from 'classnames';
import { IMAGE_TYPE } from '../types.js';
import { uniqueId, includes, get, isFunction } from 'lodash';
import { addAnchorTagToUrl, anchorScroll, isSSR } from 'wix-rich-content-common';
import { Image as Image$1 } from 'wix-rich-content-ui-components';
import { RICOS_DEFAULTS } from '../consts.js';
import expand from '../icons/expand.js';
import InPluginInput from '../InPluginInput.js';
import { s as styleInject } from '../style-inject.es-746bb8ed.js';
import 'ricos-schema';

var css_248z = ".glob_fontElementMap,.hvnYX{font-family:var(--ricos-font-family,unset)}.ms0jY{color:var(--ricos-custom-p-color,unset);display:block;font-family:var(--ricos-custom-p-font-family,unset);font-size:var(--ricos-custom-p-font-size,unset);font-style:var(--ricos-custom-p-font-style,unset);font-weight:var(--ricos-custom-p-font-weight,unset);line-height:var(--ricos-custom-p-line-height,unset);margin-bottom:12px;margin-top:12px;min-height:var(--ricos-custom-p-min-height,unset);overflow:hidden;position:relative;text-decoration:var(--ricos-custom-p-text-decoration,unset)}.ms0jY>span>span,span>a span{background-color:var(--ricos-custom-p-background-color,unset)}@supports not (contain:inline-size){@media only screen and (max-width:640px){.ms0jY{font-size:var(--ricos-custom-p-font-size,unset)}}}@container (width < 640px){.ms0jY{font-size:var(--ricos-custom-p-font-size,unset)}}.wRo5n{background-color:#fff;border-radius:30px;display:none;height:28px;position:absolute;right:12px;top:12px;width:28px;z-index:10}.ms0jY:hover .wRo5n{align-items:center;display:flex;justify-content:center}.IWEZ0{height:16px;position:absolute;width:16px;z-index:11}.IEX-8{cursor:pointer}.sGUMk{overflow:hidden}.KinKw{position:relative;width:100%}.KinKw:before{content:\"\";display:block;padding-bottom:calc(var(--dim-height)/var(--dim-width)*100%);width:100%}.KinKw>:first-child{bottom:0;left:0;position:absolute;right:0;top:0}.NmTiS{display:block;height:auto;width:100%}.YJDVM{bottom:0;left:0;position:absolute;right:0;top:0}.zSFtO{background:none;color:var(--ricos-custom-p-color,unset);color:var(--ricos-custom-p-color,var(--ricos-text-color,#212121));display:block;font-family:var(--ricos-custom-p-font-family,unset);font-size:var(--ricos-custom-p-font-size,unset);font-size:14px;font-style:var(--ricos-custom-p-font-style,unset);font-weight:var(--ricos-custom-p-font-weight,unset);line-height:var(--ricos-custom-p-line-height,unset);min-height:var(--ricos-custom-p-min-height,unset);padding:18px 0;text-align:center;text-decoration:var(--ricos-custom-p-text-decoration,unset)}.zSFtO>span>span,span>a span{background-color:var(--ricos-custom-p-background-color,unset)}@supports not (contain:inline-size){@media only screen and (max-width:640px){.zSFtO{font-size:var(--ricos-custom-p-font-size,unset)}}}@container (width < 640px){.zSFtO{font-size:var(--ricos-custom-p-font-size,unset)}}.j-Rvx{filter:blur(8px);position:relative}.fGDQK,.j-Rvx{transition:opacity .8s ease}.fGDQK{left:0;opacity:0;position:absolute;top:0}.fGDQK.E4sXE{opacity:1;position:relative}@keyframes Mrbcp{0%{left:-5%;width:5%}50%{left:25%;width:50%}to{left:100%;width:5%}}";
var styles = {"fontElementMap":"hvnYX","imageContainer":"ms0jY","expandContainer":"wRo5n","expandIcon":"IWEZ0","pointer":"IEX-8","imageWrapper":"sGUMk","imageRatio":"KinKw","image":"NmTiS","imageOverlay":"YJDVM","imageCaption":"zSFtO","imagePreload":"j-Rvx","imageHighres":"fGDQK","onlyHighRes":"E4sXE","image_loading":"Mrbcp"};
styleInject(css_248z);

var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
const IMAGE_FALLBACK_WIDTH = 300;
const getImageSrc = (src, customGetImageUrl) => {
  if (typeof src !== "object") {
    return src;
  }
  if (src == null ? void 0 : src.url) {
    return src.url;
  } else if (src == null ? void 0 : src.id) {
    if (customGetImageUrl) {
      return customGetImageUrl({ file_name: src.id });
    }
    return src.id;
  }
  return "";
};
class ImageViewer extends React.Component {
  constructor(props) {
    super(props);
    this.handleExpand = (e) => {
      var _a;
      e.preventDefault();
      const {
        settings: { onExpand },
        helpers = {}
      } = this.props;
      (_a = helpers.onViewerAction) == null ? void 0 : _a.call(helpers, IMAGE_TYPE, "Click", "expand_image");
      this.hasExpand() && (onExpand == null ? void 0 : onExpand(this.props.node.id));
    };
    this.scrollToAnchor = (e) => {
      const {
        node: { imageData: { link } = {} },
        customAnchorScroll
      } = this.props;
      const anchor = link == null ? void 0 : link.anchor;
      if (customAnchorScroll) {
        return customAnchorScroll(e, anchor);
      }
      const anchorString = `viewer-${anchor}`;
      const element = document.getElementById(anchorString);
      if (!element) {
        return;
      }
      addAnchorTagToUrl(anchorString);
      anchorScroll(element);
    };
    this.hasLink = () => {
      var _a, _b;
      return (_b = (_a = this.props.node.imageData) == null ? void 0 : _a.link) == null ? void 0 : _b.url;
    };
    this.hasAnchor = () => {
      var _a, _b;
      return (_b = (_a = this.props.node.imageData) == null ? void 0 : _a.link) == null ? void 0 : _b.anchor;
    };
    this.onKeyDown = (e) => {
      if (e.key === "Enter" || e.key === " ") {
        this.handleClick(e);
      }
    };
    this.handleClick = (e) => {
      if (this.hasLink()) {
        return null;
      } else if (this.hasAnchor()) {
        e.preventDefault();
        e.stopPropagation();
        this.scrollToAnchor(e);
      } else {
        this.handleExpand(e);
      }
    };
    this.handleContextMenu = (e) => {
      const {
        node: { imageData: { disableDownload = false } = {} }
      } = this.props;
      return disableDownload && e.preventDefault();
    };
    this.hasExpand = () => {
      const {
        node: { imageData },
        settings
      } = this.props;
      let disableExpand = false;
      if ((imageData == null ? void 0 : imageData.disableExpand) !== void 0) {
        disableExpand = imageData.disableExpand;
      } else if (settings.disableExpand !== void 0) {
        disableExpand = settings.disableExpand;
      }
      return !disableExpand && settings.onExpand;
    };
    this.renderExpandIcon = () => {
      return /* @__PURE__ */ React.createElement("div", { className: styles.expandContainer }, /* @__PURE__ */ React.createElement(expand, { className: styles.expandIcon, onClick: this.handleExpand }));
    };
    this.getImgSize = (imgSrc) => {
      const newImg = new Image();
      newImg.onload = () => this.setState({ dimensions: { width: newImg.width, height: newImg.height } });
      newImg.src = imgSrc;
    };
    this._id = uniqueId("new-image");
  }
  getImageDataUrl() {
    return this.props.dataUrl ? this.props.dataUrl : null;
  }
  renderCaption(caption) {
    const { onCaptionChange, setFocusToBlock } = this.props;
    const classes = classNames(styles.imageCaption, this.hasLink() && styles.link);
    return onCaptionChange ? /* @__PURE__ */ React.createElement(
      InPluginInput,
      {
        className: classes,
        value: caption,
        onChange: onCaptionChange,
        setFocusToBlock
      }
    ) : /* @__PURE__ */ React.createElement("span", { dir: "auto", className: classes }, caption);
  }
  shouldRenderCaption() {
    var _a, _b, _c, _d;
    const { settings, node, defaultCaption } = this.props;
    const caption = (_a = node.imageData) == null ? void 0 : _a.caption;
    if (includes(get(settings, "toolbar.hidden"), "settings")) {
      return false;
    }
    if (!caption || caption === defaultCaption) {
      return false;
    }
    const { containerData } = node.imageData || RICOS_DEFAULTS;
    if (((_b = containerData == null ? void 0 : containerData.width) == null ? void 0 : _b.size) === "ORIGINAL" && ((_d = (_c = node.imageData) == null ? void 0 : _c.image) == null ? void 0 : _d.width)) {
      return node.imageData.image.width >= 350;
    }
    return true;
  }
  // eslint-disable-next-line complexity
  render() {
    var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j;
    const {
      node,
      className,
      settings,
      setComponentUrl,
      helpers,
      isMobile,
      seoMode,
      accessibilityProps,
      onCaptionChange
    } = this.props;
    const { containerData } = node.imageData || RICOS_DEFAULTS;
    const { image, altText } = node.imageData || {};
    const { src, width, height } = image || {};
    const itemClassName = classNames(styles.imageWrapper, styles.imageRatio, className, {
      [styles.pointer]: this.hasExpand()
    });
    const imageSrc = this.getImageDataUrl() || getImageSrc(src, helpers == null ? void 0 : helpers.getImageUrl) || "";
    let imageProps = {};
    if (src && settings && settings.imageProps) {
      imageProps = isFunction(settings.imageProps) ? settings.imageProps({ url: src.url, file_name: src.id, width, height }) : settings.imageProps;
    }
    const socialAttrs = {};
    ["data-pin-nopin", "data-pin-url", "data-pin-media"].forEach(
      (attr) => imageProps[attr] && (socialAttrs[attr] = imageProps[attr])
    );
    setComponentUrl == null ? void 0 : setComponentUrl(imageSrc);
    const usePredefinedWidth = ((containerData == null ? void 0 : containerData.alignment) === "LEFT" || (containerData == null ? void 0 : containerData.alignment) === "RIGHT") && !((_a = containerData.width) == null ? void 0 : _a.custom) && ((_b = containerData.width) == null ? void 0 : _b.size) !== "SMALL" && ((_c = containerData.width) == null ? void 0 : _c.size) !== "ORIGINAL" && !isMobile;
    const dim = {
      width: width || ((_d = containerData == null ? void 0 : containerData.width) == null ? void 0 : _d.custom) || ((_e = this.state) == null ? void 0 : _e.dimensions.width),
      height: height || ((_f = containerData == null ? void 0 : containerData.height) == null ? void 0 : _f.custom) || ((_g = this.state) == null ? void 0 : _g.dimensions.height)
    };
    const isDimensionsExists = dim.width && dim.height;
    if (!isDimensionsExists && !isSSR()) {
      this.getImgSize(imageSrc);
    }
    const customWidthStyle = ((_h = containerData == null ? void 0 : containerData.width) == null ? void 0 : _h.size) === "ORIGINAL" || isMobile && (Number((_i = containerData == null ? void 0 : containerData.width) == null ? void 0 : _i.custom) || 0) > 150 ? { width: width != null ? width : "0px", maxWidth: "100%" } : {};
    const isEditor = !!onCaptionChange;
    return /* @__PURE__ */ React.createElement(
      "div",
      __spreadValues({
        "data-hook": "imageViewer",
        className: styles.imageContainer,
        onContextMenu: this.handleContextMenu,
        onKeyDown: this.onKeyDown
      }, accessibilityProps),
      /* @__PURE__ */ React.createElement("div", { "aria-label": altText, onClick: this.handleClick, onKeyDown: this.onKeyDown }, isDimensionsExists && /* @__PURE__ */ React.createElement(
        Image$1,
        __spreadValues({
          id: imageSrc,
          containerId: this._id,
          displayMode: "fill",
          width: dim.width,
          height: dim.height,
          uri: imageSrc,
          alt: altText || "",
          containerProps: {
            className: itemClassName,
            containerId: this._id,
            style: __spreadValues(__spreadValues({
              ["--dim-height"]: dim.height,
              ["--dim-width"]: dim.width
            }, usePredefinedWidth && { width: IMAGE_FALLBACK_WIDTH + "px" }), customWidthStyle)
          },
          imageStyles: {
            maxWidth: "100%"
          },
          socialAttrs,
          isSEOBot: !!seoMode
        }, !isEditor ? {
          placeholderTransition: seoMode ? void 0 : "blur",
          shouldUseLQIP: !settings.disableTransition
        } : {})
      ), this.hasExpand() && this.renderExpandIcon()),
      this.shouldRenderCaption() && this.renderCaption((_j = node.imageData) == null ? void 0 : _j.caption)
    );
  }
}

export { ImageViewer };
//# sourceMappingURL=image-viewer.js.map
